import { useEffect, useMemo, useCallback } from 'react';
import { AnimatePresence } from 'motion/react';
import { MapView } from './components/MapView';
import { JournalView } from './components/JournalView';
import { PlacePopup } from './components/PlacePopup';
import { AppHeader } from './components/AppHeader';
import { StatsBar } from './components/StatsBar';
import { TimelineControl } from './components/TimelineControl';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { usePlaces } from './hooks/usePlaces';
import { useAnimation } from './hooks/useAnimation';
import { useMarkerState } from './hooks/useMarkerState';
import { useTimeMachine } from './hooks/useTimeMachine';
import { useTimeoutRef } from './hooks/useTimeoutRef';
import { useAppState } from './hooks/useAppState';
import { useAppHandlers } from './hooks/useAppHandlers';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import type { Place } from './types/Place';
import type { MapTheme } from '@love-map/shared';

interface AppProps {
  /** Optional map ID for loading places from a specific map */
  mapId?: string;
  /** SPRINT-4.6: Callback when user wants to edit a place */
  onEditPlace?: (place: Place) => void;
  /** SPRINT-5.2: Pre-loaded places for public map view (bypasses usePlaces) */
  initialPlaces?: Place[];
  /** SPRINT-5.2: Custom map title for public maps */
  mapTitle?: string;
  /** SPRINT-5.5: Custom map theme settings */
  mapTheme?: MapTheme;
}

export function App({ mapId, onEditPlace, initialPlaces, mapTitle, mapTheme }: AppProps) {
  // Feature flags from centralized config
  const features = useFeatureFlags();

  // Centralized UI state
  const appState = useAppState();
  const {
    activeView,
    mapReady,
    setMapReady,
    selectedPlace,
    setSelectedPlace,
    highlightedPlaceId,
    targetPlaceId,
    navigationHighlight,
    activeLayer,
    setActiveLayer,
    statusMessage,
    galleryIndex,
    setGalleryIndex,
    isFullscreen,
    setIsFullscreen,
    collapsedMonths,
    resetViewTrigger,
  } = appState;

  // Timeout hooks for auto-clearing highlights (with automatic cleanup)
  const [setHighlightTimer] = useTimeoutRef();
  const [setNavigationTimer, clearNavigationTimer] = useTimeoutRef();

  // SPRINT-5.2: Use initialPlaces if provided (public map view), otherwise fetch
  const placesResult = usePlaces(initialPlaces ? undefined : mapId);

  // Use initialPlaces when provided, otherwise use fetched places
  const places = initialPlaces ?? placesResult.places;
  const isLoading = initialPlaces ? false : placesResult.isLoading;
  const placesError = initialPlaces ? null : placesResult.error;
  const retry = placesResult.retry;
  const canRetry = initialPlaces ? false : placesResult.canRetry;

  // Calculate stats from places (works with both initialPlaces and fetched)
  const stats = useMemo(() => {
    const countries = new Set(places.map(p => p.country));
    return { countriesCount: countries.size, placesCount: places.length };
  }, [places]);

  // Create getPlaceById helper
  const getPlaceById = useCallback((id: string): Place | undefined => {
    return places.find(p => p.id === id);
  }, [places]);

  const { animationState, visiblePlaces, startAnimation, resetAnimation } = useAnimation(places);

  // Filter places by active layer (Story 5.3)
  // NOTE: All hooks must be called before any early returns (React Rules of Hooks)
  const filteredPlaces = useMemo(() => {
    if (activeLayer === 'all') return places;
    return places.filter(place => place.layer === activeLayer);
  }, [places, activeLayer]);

  // Epic 9: Time Machine hook - uses filtered places so timeline adjusts to active filter
  const timeMachine = useTimeMachine(filteredPlaces);
  const {
    isTimeMachineMode,
    enterTimeMachine,
    exitTimeMachine,
    progress: timeMachineProgress,
    setProgress: setTimeMachineProgress,
    isPlaying: isTimeMachinePlaying,
    togglePlayPause: toggleTimeMachinePlayPause,
    visiblePlaces: timeMachineVisiblePlaces,
    monthMarkers,
    currentDateLabel,
    hasTimelinePlaces,
    pause: pauseTimeMachine,
    advanceToNextPlace,
  } = timeMachine;

  // Combine animation/time machine visibility with layer filter
  const effectiveVisiblePlaces = useMemo(() => {
    // In Time Machine mode, use time machine's visible places (already filtered by layer)
    if (isTimeMachineMode) {
      return timeMachineVisiblePlaces;
    }

    // In normal mode, filter animation's visible places by active layer
    if (!visiblePlaces) return undefined;

    const filtered = new Set<string>();
    visiblePlaces.forEach(id => {
      const place = places.find(p => p.id === id);
      if (place && (activeLayer === 'all' || place.layer === activeLayer)) {
        filtered.add(id);
      }
    });
    return filtered;
  }, [visiblePlaces, timeMachineVisiblePlaces, isTimeMachineMode, places, activeLayer]);

  const {
    getMarkerState,
    handlePopupClose: handleMarkerPopupClose,
    markPreviousAsViewed,
    markVisited,
    clearHighlight,
    reset: resetMarkerState,
    highlightedPlace, // Epic 9: For cluster glow when containing highlighted marker
  } = useMarkerState(places, animationState, selectedPlace, filteredPlaces);

  // Extracted handler logic (reduces App component complexity)
  const {
    handleMarkerClick,
    handlePopupClose,
    handleMapReady,
    handleReplayAnimation,
    handleEnterTimeMachine,
    handleExitTimeMachine,
    handleTimeMachineProgressChange,
    handleLogoClick,
    handleViewToggle,
    handleShowInJournal,
    handleShowOnMap,
    handleMapInteraction,
    handleCenterComplete,
    handleToggleMonthCollapse,
    getEnhancedMarkerState,
    mapPanelRef,
    journalPanelRef,
  } = useAppHandlers({
    setActiveView: appState.setActiveView,
    setMapReady,
    setSelectedPlace,
    setHighlightedPlaceId: appState.setHighlightedPlaceId,
    setTargetPlaceId: appState.setTargetPlaceId,
    setNavigationHighlight: appState.setNavigationHighlight,
    setActiveLayer,
    setStatusMessage: appState.setStatusMessage,
    setCollapsedMonths: appState.setCollapsedMonths,
    triggerResetView: appState.triggerResetView,
    selectedPlace,
    navigationHighlight,
    setHighlightTimer,
    setNavigationTimer,
    clearNavigationTimer,
    animationState,
    resetAnimation,
    getMarkerState,
    handleMarkerPopupClose,
    markPreviousAsViewed,
    markVisited,
    clearHighlight,
    resetMarkerState,
    isTimeMachineMode,
    enterTimeMachine,
    exitTimeMachine,
    pauseTimeMachine,
    setTimeMachineProgress,
    advanceToNextPlace,
    hasTimelinePlaces,
  });

  // Start animation when map is ready (AC4: within 1 second of map ready)
  useEffect(() => {
    if (mapReady && animationState === 'idle') {
      startAnimation();
    }
  }, [mapReady, animationState, startAnimation]);

  // Story 5.3: Close popup if selected place is filtered out by layer change
  useEffect(() => {
    if (selectedPlace && activeLayer !== 'all') {
      const place = getPlaceById(selectedPlace);
      if (place && place.layer !== activeLayer) {
        setSelectedPlace(null);
      }
    }
  }, [activeLayer, selectedPlace, getPlaceById, setSelectedPlace]);

  // Story 6.1: Reset gallery index when selected place changes
  // Story 6.4: Also reset fullscreen state
  useEffect(() => {
    setGalleryIndex(0);
    setIsFullscreen(false);
  }, [selectedPlace, setGalleryIndex, setIsFullscreen]);

  // Feature-gated props: Time Machine is only available when the flag is enabled
  const timeMachineEnabled = features.timeMachine && hasTimelinePlaces;
  // Feature-gated props: Animation replay
  const showReplay = features.animationReplay && animationState === 'complete' && !isTimeMachineMode;
  // Feature-gated props: Fullscreen photos
  const fullscreenEnabled = features.photoFullscreen;

  const currentPlace = selectedPlace ? getPlaceById(selectedPlace) : null;

  // Early returns for loading/error states (placed after all hooks per React Rules of Hooks)
  if (isLoading) {
    return <LoadingSkeleton variant="full" message="Loading your memories..." />;
  }

  if (placesError) {
    return (
      <div
        className="min-h-screen bg-bg-base flex items-center justify-center p-4"
        role="alert"
        aria-live="assertive"
      >
        <div className="max-w-md w-full bg-bg-surface rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4" aria-hidden="true">
            🗺️
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">
            Could not load places
          </h1>
          <p className="text-gray-400 mb-6">
            There was a problem loading the travel data. Please try again.
          </p>
          <details className="text-left mb-6">
            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400">
              Technical details
            </summary>
            <pre className="mt-2 p-3 bg-bg-base rounded text-xs text-red-400 overflow-auto max-h-32">
              {placesError.message}
            </pre>
          </details>
          <div className="flex gap-3 justify-center">
            {canRetry && (
              <button
                onClick={retry}
                className="px-6 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                Try Again
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-bg-elevated hover:bg-bg-elevated/80 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-bg-base">
      <AppHeader
        activeView={activeView}
        onViewToggle={handleViewToggle}
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        title={mapTitle}
        showReplay={showReplay}
        onReplay={handleReplayAnimation}
        disabled={animationState !== 'complete' || isTimeMachineMode}
        onLogoClick={handleLogoClick}
        onTimeMachine={timeMachineEnabled ? handleEnterTimeMachine : undefined}
        isTimeMachineMode={isTimeMachineMode}
      />

      {/* H6: Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {/* Main content with top padding for header (includes layer selector row on mobile) and bottom padding for stats bar */}
      <main className="pt-24 lg:pt-16 pb-14 lg:pb-16 h-full">
        <AnimatePresence mode="wait">
          {activeView === 'map' || !features.journalView ? (
            <div
              ref={mapPanelRef}
              id="panel-map"
              role="tabpanel"
              aria-labelledby="tab-map"
              tabIndex={0}
              className="h-full outline-none"
            >
              <MapView
                key="map"
                onMarkerClick={handleMarkerClick}
                onMapReady={handleMapReady}
                animationState={animationState}
                visiblePlaces={effectiveVisiblePlaces}
                getMarkerState={getEnhancedMarkerState}
                targetPlaceId={targetPlaceId}
                onCenterComplete={handleCenterComplete}
                onMapInteraction={handleMapInteraction}
                activeLayer={activeLayer}
                resetViewTrigger={resetViewTrigger}
                highlightedPlaceId={navigationHighlight || highlightedPlace}
                isTimeMachineMode={isTimeMachineMode}
                mapTheme={mapTheme}
              />
            </div>
          ) : (
            <div
              ref={journalPanelRef}
              id="panel-journal"
              role="tabpanel"
              aria-labelledby="tab-journal"
              tabIndex={0}
              className="h-full outline-none"
            >
              <JournalView
                key="journal"
                places={filteredPlaces}
                onShowOnMap={handleShowOnMap}
                highlightedPlaceId={highlightedPlaceId}
                collapsedMonths={collapsedMonths}
                onToggleMonthCollapse={handleToggleMonthCollapse}
              />
            </div>
          )}
        </AnimatePresence>
      </main>

      {currentPlace && (
        <PlacePopup
          place={currentPlace}
          isOpen={true}
          onClose={handlePopupClose}
          onShowInJournal={features.journalView ? handleShowInJournal : undefined}
          galleryIndex={galleryIndex}
          onGalleryIndexChange={setGalleryIndex}
          isFullscreen={fullscreenEnabled ? isFullscreen : false}
          onFullscreenChange={fullscreenEnabled ? setIsFullscreen : undefined}
          onEdit={onEditPlace}
        />
      )}

      {/* Epic 9: Time Machine timeline control - only shown when feature is enabled */}
      {features.timeMachine && (
        <TimelineControl
          progress={timeMachineProgress}
          onProgressChange={handleTimeMachineProgressChange}
          isPlaying={isTimeMachinePlaying}
          onPlayPause={toggleTimeMachinePlayPause}
          onClose={handleExitTimeMachine}
          monthMarkers={monthMarkers}
          currentDateLabel={currentDateLabel}
          isVisible={isTimeMachineMode}
        />
      )}

      <StatsBar
        countriesCount={stats.countriesCount}
        placesCount={stats.placesCount}
      />
    </div>
  );
}
